import { useState } from "react";
import type { CSSProperties } from "react";
import type { BillingSnapshot } from "../../hooks/useBillingSnapshots";
import { colors, fontSize, radius, shadow, space } from "../../lib/tokens";

interface MenuItem {
  key: string;
  label: string;
  danger?: boolean;
}

function actionsFor(status: BillingSnapshot["status"]): MenuItem[] {
  switch (status) {
    case "draft":
      return [
        { key: "send", label: "Enviar cobrança" },
        { key: "regenerate", label: "Regenerar" },
        { key: "void", label: "Anular", danger: true },
      ];
    case "sent":
      return [
        { key: "mark_paid", label: "Marcar como paga" },
        { key: "cancel", label: "Cancelar cobrança", danger: true },
        { key: "void", label: "Anular", danger: true },
      ];
    case "paid":
      return [
        { key: "refund", label: "Reembolsar", danger: true },
        { key: "view", label: "Ver detalhes" },
      ];
    case "overdue":
      return [
        { key: "mark_paid", label: "Marcar como paga" },
        { key: "cancel", label: "Cancelar cobrança", danger: true },
        { key: "void", label: "Anular", danger: true },
      ];
    case "voided":
      return [{ key: "view", label: "Ver detalhes" }];
    case "refunded":
      return [{ key: "view", label: "Ver detalhes" }];
    default:
      return [];
  }
}

interface InvoiceActionMenuProps {
  snapshot: BillingSnapshot;
  onAction: (action: string) => void;
}

export function InvoiceActionMenu({ snapshot, onAction }: InvoiceActionMenuProps) {
  const [open, setOpen] = useState(false);
  const items = actionsFor(snapshot.status);
  if (items.length === 0) return null;

  const trigger: CSSProperties = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: space.xs,
    color: colors.stone,
    fontSize: fontSize.lg,
    lineHeight: 1,
  };
  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 99,
  };
  const menu: CSSProperties = {
    position: "absolute",
    right: 0,
    top: "100%",
    marginTop: space.xs,
    zIndex: 100,
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    boxShadow: shadow.md,
    minWidth: 180,
    overflow: "hidden",
  };
  const itemStyle = (danger?: boolean): CSSProperties => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: `${space.sm}px ${space.md}px`,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: fontSize.sm,
    color: danger ? colors.error : colors.ink,
  });

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="Ações"
        style={trigger}
      >
        ⋯
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={overlay} />
          <div style={menu}>
            {items.map((i) => (
              <button
                key={i.key}
                onClick={() => {
                  setOpen(false);
                  onAction(i.key);
                }}
                style={itemStyle(i.danger)}
              >
                {i.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
